import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, Circle, Clock, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

type StepKey =
  | "contact" | "payment" | "w9" | "teaching_agreement" | "bg_consent" | "review";

// Standards of Conduct and payment terms are both covered
// by the annual Teaching Agreement and are no longer collected separately.
const STEPS: { key: StepKey; label: string }[] = [
  { key: "contact", label: "Contact Information" },
  { key: "payment", label: "Payment Information" },
  { key: "w9", label: "Tax Information" },
  { key: "teaching_agreement", label: "Annual Teaching Agreement" },
  { key: "bg_consent", label: "Background Check Consent" },
  { key: "review", label: "Review & Submit" },
];

export default function InstructorOnboarding() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<any>(null);

  useEffect(() => {
    if (!token) { setError("Missing onboarding link."); setLoading(false); return; }
    (async () => {
      const res = await callPortal({ action: "init", token });
      if (res.error) { setError(errorLabel(res.error)); setLoading(false); return; }
      setState(res);
      setLoading(false);
    })();
  }, [token]);

  const call = async (payload: any) => {
    const res = await callPortal({ ...payload, token });
    if (res.error) {
      toast.error(errorLabel(res.error));
      return null;
    }
    setState(res);
    return res;
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="max-w-md w-full">
        <CardHeader><CardTitle>Onboarding link unavailable</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-sm mt-4">
            If you believe this is an error, contact silversagehomeschoolhubs@gmail.com
            to request a new secure link.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  return <OnboardingPortalView state={state} call={call} />;
}

/**
 * The instructor-facing onboarding experience.
 * Shared by the live portal (`/instructor-onboarding?token=…`) and the
 * admin "Preview Onboarding Packet" dialog so the preview can never drift
 * from what instructors actually see.
 */
export function OnboardingPortalView({
  state, call, readOnly = false,
}: { state: any; call: (payload: any) => Promise<any>; readOnly?: boolean }) {
  const [activeStep, setActiveStep] = useState<StepKey>("contact");
  const s = state.steps;
  const alreadySubmitted = state.packet.overall_status === "submitted_for_review"
    || state.packet.overall_status === "complete";
  const guardedCall = readOnly
    ? async () => { toast.info("Preview only — nothing was saved or sent."); return null; }
    : call;

  return (
    <div className={readOnly ? "bg-background" : "min-h-screen bg-background"}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">Silver Sage Homeschool Hub — Instructor Onboarding</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Welcome{state.instructor?.preferred_name ? `, ${state.instructor.preferred_name}` : ""}.
            Please complete each section. You can save and resume anytime with this link.
          </p>
        </div>

        {alreadySubmitted && (
          <Alert className="mb-4">
            <AlertTitle>Packet submitted</AlertTitle>
            <AlertDescription>
              Silver Sage has your packet and will follow up if anything needs changes.
            </AlertDescription>
          </Alert>
        )}

        {/* Stepper */}
        <div className="grid md:grid-cols-4 gap-2 mb-6">
          {STEPS.map((st) => {
            const complete = st.key === "review"
              ? s.contact.complete && s.payment.complete
                && s.teaching_agreement.complete && s.bg_consent.complete
              : (s as any)[st.key]?.complete;
            return (
              <button
                key={st.key}
                onClick={() => setActiveStep(st.key)}
                className={`text-left p-3 rounded border ${activeStep === st.key ? "border-primary bg-primary/5" : "border-muted"} `}
              >
                <div className="flex items-center gap-2 text-xs">
                  {complete ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Circle className="h-4 w-4" />}
                  <span className="font-medium">{st.label}</span>
                </div>
              </button>
            );
          })}
        </div>

        {activeStep === "contact" && <ContactStep state={state} call={guardedCall} />}
        {activeStep === "payment" && <PaymentStep state={state} call={guardedCall} />}

        {activeStep === "w9" && <W9Step state={state} call={guardedCall} />}
        {activeStep === "teaching_agreement" && <TeachingStep state={state} call={guardedCall} />}
        {activeStep === "bg_consent" && <BgConsentStep state={state} call={guardedCall} />}
        {activeStep === "review" && <ReviewStep state={state} call={guardedCall} />}
      </div>
    </div>
  );
}


async function callPortal(body: any) {
  const { data, error } = await supabase.functions.invoke("instructor-onboarding-portal", { body });
  if (error) return { error: error.message };
  return data;
}
function errorLabel(err: string) {
  if (err === "expired") return "This onboarding link has expired. Please contact Silver Sage to request a new one.";
  if (err === "revoked") return "This onboarding link has been revoked. Please contact Silver Sage.";
  if (err === "invalid_token") return "Invalid onboarding link.";
  return err;
}

function ContactStep({ state, call }: any) {
  const initial = state.steps.contact.data ?? {};
  const instr = state.instructor ?? {};
  const [form, setForm] = useState({
    legal_name: initial.legal_name ?? instr.full_name ?? "",
    preferred_name: initial.preferred_name ?? instr.preferred_name ?? "",
    email: initial.email ?? instr.email ?? "",
    phone: initial.phone ?? instr.phone ?? "",
    mailing_address_line1: initial.mailing_address_line1 ?? "",
    mailing_address_line2: initial.mailing_address_line2 ?? "",
    city: initial.city ?? "", state: initial.state ?? "", zip: initial.zip ?? "",
    emergency_contact_name: initial.emergency_contact_name ?? "",
    emergency_contact_phone: initial.emergency_contact_phone ?? "",
  });
  const locked = state.steps.contact.locked;
  return (
    <Card>
      <CardHeader><CardTitle>Contact Information</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {locked && <Alert><AlertDescription>Submitted. Contact Silver Sage to request changes.</AlertDescription></Alert>}
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Legal name" value={form.legal_name} onChange={(v) => setForm({ ...form, legal_name: v })} disabled={locked} />
          <Field label="Preferred name" value={form.preferred_name} onChange={(v) => setForm({ ...form, preferred_name: v })} disabled={locked} />
          <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} disabled={locked} />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} disabled={locked} />
          <Field label="Mailing address" value={form.mailing_address_line1} onChange={(v) => setForm({ ...form, mailing_address_line1: v })} disabled={locked} />
          <Field label="Address line 2" value={form.mailing_address_line2} onChange={(v) => setForm({ ...form, mailing_address_line2: v })} disabled={locked} />
          <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} disabled={locked} />
          <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} disabled={locked} />
          <Field label="ZIP" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} disabled={locked} />
          <Field label="Emergency contact name" value={form.emergency_contact_name} onChange={(v) => setForm({ ...form, emergency_contact_name: v })} disabled={locked} />
          <Field label="Emergency contact phone" value={form.emergency_contact_phone} onChange={(v) => setForm({ ...form, emergency_contact_phone: v })} disabled={locked} />
        </div>
        {!locked && (
          <div className="flex gap-2 pt-3">
            <Button variant="outline" onClick={async () => { const r = await call({ action: "save_contact", data: form }); if (r) toast.success("Saved"); }}>Save Draft</Button>
            <Button onClick={async () => { const r = await call({ action: "save_contact", data: form, submit: true }); if (r) toast.success("Contact submitted"); }}>Save & Continue</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentStep({ state, call }: any) {
  const initial = state.steps.payment.data ?? {};
  const locked = state.steps.payment.locked;
  const [form, setForm] = useState({
    zelle_contact: initial.zelle_contact ?? "",
    zelle_name: initial.zelle_name ?? "",
    payment_notes: initial.payment_notes ?? "",
  });
  const contactOk = form.zelle_contact.trim().length > 0;
  const nameOk = form.zelle_name.trim().length > 0;
  const canSubmit = contactOk && nameOk;
  const payload = {
    payment_method: "zelle",
    zelle_contact: form.zelle_contact.trim(),
    zelle_name: form.zelle_name.trim(),
    payment_notes: form.payment_notes,
  };
  return (
    <Card>
      <CardHeader><CardTitle>Payment Information</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <Alert variant="default">
          <AlertDescription>
            Silver Sage Homeschool Hub pays instructors via Zelle. We do not collect bank
            account credentials or debit card numbers. Please provide the Zelle information
            where you would like to receive your payments.
          </AlertDescription>
        </Alert>
        {locked && <Alert><AlertDescription>Submitted. Contact Silver Sage to request changes.</AlertDescription></Alert>}
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Zelle phone or email" value={form.zelle_contact} onChange={(v) => setForm({ ...form, zelle_contact: v })} disabled={locked} />
          <Field label="Name on Zelle" value={form.zelle_name} onChange={(v) => setForm({ ...form, zelle_name: v })} disabled={locked} />
        </div>
        <div>
          <Label>Notes (optional)</Label>
          <Textarea value={form.payment_notes} disabled={locked} onChange={(e) => setForm({ ...form, payment_notes: e.target.value })} />
        </div>
        {!locked && (
          <div className="space-y-2 pt-3">
            {!canSubmit && (
              <p className="text-sm text-muted-foreground">
                Both the Zelle phone or email and the name on Zelle are required before this step can be submitted.
              </p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={async () => { const r = await call({ action: "save_payment", data: payload }); if (r) toast.success("Saved"); }}>Save Draft</Button>
              <Button
                disabled={!canSubmit}
                onClick={async () => { const r = await call({ action: "save_payment", data: payload, submit: true }); if (r) toast.success("Payment submitted"); }}
              >Save &amp; Continue</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function W9Step({ state, call }: any) {
  const w9 = state.steps.w9;
  const [method, setMethod] = useState<"online" | "pdf">("online");
  const [uploading, setUploading] = useState(false);

  // If already complete, show status card (allow re-submit if rejected)
  const isComplete = w9.complete && w9.verification_status !== "rejected";
  const isRejected = w9.verification_status === "rejected";

  if (isComplete) {
    return (
      <Card>
        <CardHeader><CardTitle>Tax Information</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Alert>
            <AlertTitle>W-9 received</AlertTitle>
            <AlertDescription>
              {w9.collection_method === "online" ? "Submitted online" : w9.collection_method === "pdf_upload" ? "Signed PDF uploaded" : "On file"}
              {w9.tin_last4 && <> · {(w9.tin_type ?? "TIN").toUpperCase()} ending in {w9.tin_last4}</>}
              {" · "}
              {w9.verification_status === "verified" ? "Verified" : "Pending admin review"}
            </AlertDescription>
          </Alert>
          <p className="text-xs text-muted-foreground">
            Your Tax ID is encrypted at rest. Only the last four digits are visible in day-to-day admin views.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader><CardTitle>Tax Information</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {isRejected && (
          <Alert variant="destructive">
            <AlertTitle>Please resubmit</AlertTitle>
            <AlertDescription>
              {w9.verification_notes || "The prior W-9 could not be verified. Please submit again."}
            </AlertDescription>
          </Alert>
        )}
        <div className="p-3 border rounded bg-muted/40 text-sm space-y-2">
          <p><strong>Why we need this.</strong> Silver Sage is required to collect a Form W-9 from every paid instructor so we can prepare a 1099-NEC at year end if your total payments meet the IRS threshold.</p>
          <p className="text-xs text-muted-foreground">Your Tax ID is encrypted at rest with AES-256. Only the last four digits appear in the normal admin interface. Full digits are only decrypted in memory during 1099 preparation, and every access is logged.</p>
        </div>

        <RadioGroup value={method} onValueChange={(v) => setMethod(v as "online" | "pdf")}>
          <div className="p-3 border rounded flex items-start gap-3">
            <RadioGroupItem value="online" id="m-online" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="m-online" className="text-sm font-medium">
                Complete W-9 Online <span className="ml-1 text-primary">★ Recommended</span>
              </Label>
              <p className="text-xs text-muted-foreground mt-1">Takes about 3 minutes. We handle everything securely.</p>
            </div>
          </div>
          <div className="p-3 border rounded flex items-start gap-3">
            <RadioGroupItem value="pdf" id="m-pdf" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="m-pdf" className="text-sm font-medium">Upload a signed W-9 PDF</Label>
              <p className="text-xs text-muted-foreground mt-1">Already have one? Upload the signed IRS Form W-9 (PDF, max 5 MB).</p>
            </div>
          </div>
        </RadioGroup>

        {method === "online" && <W9OnlineForm state={state} call={call} />}
        {method === "pdf" && <W9PdfUpload state={state} call={call} uploading={uploading} setUploading={setUploading} />}

        <p className="text-xs text-muted-foreground">
          Official IRS Form W-9 reference:{" "}
          <a className="underline" href="https://www.irs.gov/forms-pubs/about-form-w-9" target="_blank" rel="noreferrer">
            irs.gov <ExternalLink className="inline h-3 w-3" />
          </a>
        </p>
      </CardContent>
    </Card>
  );
}

function W9OnlineForm({ state, call }: any) {
  const instr = state.instructor ?? {};
  const contact = state.steps.contact?.data ?? {};
  const [form, setForm] = useState({
    legal_name: instr.full_name ?? "",
    business_name: "",
    tax_classification: "individual_sole_prop",
    llc_tax_classification: "" as "" | "c" | "s" | "p",
    other_classification_detail: "",
    exempt_payee_code: "",
    exempt_fatca_code: "",
    address_line1: contact.mailing_address_line1 ?? "",
    address_line2: contact.mailing_address_line2 ?? "",
    city: contact.city ?? "",
    state: contact.state ?? "",
    zip: contact.zip ?? "",
    tin_type: "ssn" as "ssn" | "ein",
    tin: "",
    certified: false,
    signature: "",
    signed_legal_name: "",
  });
  const [saving, setSaving] = useState(false);
  const canSubmit =
    form.legal_name.length >= 2 &&
    form.address_line1.length >= 2 &&
    form.city && form.state && form.zip &&
    /^\d{2}-?\d{3,7}$|^\d{3}-?\d{2}-?\d{4}$|^\d{9}$/.test(form.tin.replace(/\s+/g, "")) &&
    form.certified && form.signature.length >= 2 && form.signed_legal_name.length >= 2 &&
    (form.tax_classification !== "llc" || !!form.llc_tax_classification);

  return (
    <div className="space-y-3 pt-2">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Legal name (as shown on tax return)" value={form.legal_name} onChange={(v) => setForm({ ...form, legal_name: v })} />
        <Field label="Business name (if different)" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} />
      </div>
      <div>
        <Label className="text-sm">Federal tax classification</Label>
        <select
          className="mt-1 w-full border rounded px-2 py-2 text-sm bg-background"
          value={form.tax_classification}
          onChange={(e) => setForm({ ...form, tax_classification: e.target.value })}
        >
          <option value="individual_sole_prop">Individual / Sole proprietor / Single-member LLC</option>
          <option value="c_corp">C Corporation</option>
          <option value="s_corp">S Corporation</option>
          <option value="partnership">Partnership</option>
          <option value="trust_estate">Trust / Estate</option>
          <option value="llc">LLC (multi-member)</option>
          <option value="other">Other</option>
        </select>
      </div>
      {form.tax_classification === "llc" && (
        <div>
          <Label className="text-sm">LLC tax classification</Label>
          <RadioGroup value={form.llc_tax_classification} onValueChange={(v) => setForm({ ...form, llc_tax_classification: v as any })} className="flex gap-4 mt-1">
            <div className="flex items-center gap-2"><RadioGroupItem value="c" id="llc-c" /><Label htmlFor="llc-c" className="text-sm">C = C Corp</Label></div>
            <div className="flex items-center gap-2"><RadioGroupItem value="s" id="llc-s" /><Label htmlFor="llc-s" className="text-sm">S = S Corp</Label></div>
            <div className="flex items-center gap-2"><RadioGroupItem value="p" id="llc-p" /><Label htmlFor="llc-p" className="text-sm">P = Partnership</Label></div>
          </RadioGroup>
        </div>
      )}
      {form.tax_classification === "other" && (
        <Field label="Describe" value={form.other_classification_detail} onChange={(v) => setForm({ ...form, other_classification_detail: v })} />
      )}
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Exempt payee code (usually blank)" value={form.exempt_payee_code} onChange={(v) => setForm({ ...form, exempt_payee_code: v })} />
        <Field label="Exemption from FATCA code (usually blank)" value={form.exempt_fatca_code} onChange={(v) => setForm({ ...form, exempt_fatca_code: v })} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Address" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} />
        <Field label="Address line 2" value={form.address_line2} onChange={(v) => setForm({ ...form, address_line2: v })} />
        <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
        <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
        <Field label="ZIP" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
      </div>
      <div>
        <Label className="text-sm">Tax ID type</Label>
        <RadioGroup value={form.tin_type} onValueChange={(v) => setForm({ ...form, tin_type: v as any })} className="flex gap-4 mt-1">
          <div className="flex items-center gap-2"><RadioGroupItem value="ssn" id="tin-ssn" /><Label htmlFor="tin-ssn" className="text-sm">SSN</Label></div>
          <div className="flex items-center gap-2"><RadioGroupItem value="ein" id="tin-ein" /><Label htmlFor="tin-ein" className="text-sm">EIN</Label></div>
        </RadioGroup>
      </div>
      <Field
        label={form.tin_type === "ssn" ? "Social Security Number (XXX-XX-XXXX)" : "Employer Identification Number (XX-XXXXXXX)"}
        value={form.tin}
        onChange={(v) => setForm({ ...form, tin: v })}
      />
      <p className="text-xs text-muted-foreground -mt-2">Encrypted at rest. Never shown in the admin interface — only the last four digits appear.</p>

      <div className="p-3 border rounded bg-muted/40 text-xs space-y-1">
        <div className="font-medium">Part II — Certification</div>
        <p>Under penalties of perjury, I certify that:</p>
        <ol className="list-decimal list-inside space-y-0.5">
          <li>The number shown on this form is my correct taxpayer identification number.</li>
          <li>I am not subject to backup withholding.</li>
          <li>I am a U.S. citizen or other U.S. person.</li>
          <li>The FATCA code (if any) entered above is correct.</li>
        </ol>
      </div>
      <div className="flex items-start gap-2">
        <Checkbox id="w9-cert" checked={form.certified} onCheckedChange={(v) => setForm({ ...form, certified: !!v })} />
        <Label htmlFor="w9-cert" className="text-sm leading-snug">I certify the statements above under penalty of perjury.</Label>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Typed legal name" value={form.signed_legal_name} onChange={(v) => setForm({ ...form, signed_legal_name: v })} />
        <Field label="Electronic signature (type your full name)" value={form.signature} onChange={(v) => setForm({ ...form, signature: v })} />
      </div>
      <Button
        disabled={!canSubmit || saving}
        onClick={async () => {
          setSaving(true);
          try {
            const r = await call({
              action: "save_w9_online",
              legal_name: form.legal_name,
              business_name: form.business_name || null,
              tax_classification: form.tax_classification,
              llc_tax_classification: form.llc_tax_classification || null,
              other_classification_detail: form.other_classification_detail || null,
              exempt_payee_code: form.exempt_payee_code || null,
              exempt_fatca_code: form.exempt_fatca_code || null,
              address_line1: form.address_line1,
              address_line2: form.address_line2 || null,
              city: form.city, state: form.state, zip: form.zip,
              tin_type: form.tin_type,
              tin: form.tin,
              certified: true,
              signature: form.signature,
              signed_legal_name: form.signed_legal_name,
            });
            if (r) toast.success("W-9 submitted — Silver Sage will verify shortly.");
          } finally { setSaving(false); }
        }}
      >
        {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</> : "Submit W-9"}
      </Button>
    </div>
  );
}

function W9PdfUpload({ call, uploading, setUploading }: any) {
  const [file, setFile] = useState<File | null>(null);
  const [legalName, setLegalName] = useState("");
  const [last4, setLast4] = useState("");
  const [tinType, setTinType] = useState<"ssn" | "ein">("ssn");

  const upload = async () => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("PDF must be 5 MB or smaller."); return; }
    if (file.type !== "application/pdf") { toast.error("Please upload a PDF file."); return; }
    setUploading(true);
    try {
      // 1. Request signed upload URL
      const signed = await call({ action: "w9_upload_url", file_name: file.name, content_type: "application/pdf" });
      if (!signed?.upload_url) return;
      // 2. Upload the file directly to storage
      const uploadRes = await fetch(signed.upload_url, {
        method: "PUT",
        headers: { "Content-Type": "application/pdf" },
        body: file,
      });
      if (!uploadRes.ok) { toast.error("Upload failed. Please try again."); return; }
      // 3. Confirm upload
      const confirmed = await call({
        action: "w9_confirm_upload",
        storage_path: signed.storage_path,
        legal_name: legalName,
        tin_type: tinType,
        tin_last4: last4 || undefined,
      });
      if (confirmed) toast.success("W-9 uploaded — Silver Sage will verify shortly.");
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-3 pt-2">
      <Field label="Legal name (as shown on the signed form)" value={legalName} onChange={setLegalName} />
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-sm">Tax ID type</Label>
          <RadioGroup value={tinType} onValueChange={(v) => setTinType(v as any)} className="flex gap-4 mt-1">
            <div className="flex items-center gap-2"><RadioGroupItem value="ssn" id="pdf-ssn" /><Label htmlFor="pdf-ssn" className="text-sm">SSN</Label></div>
            <div className="flex items-center gap-2"><RadioGroupItem value="ein" id="pdf-ein" /><Label htmlFor="pdf-ein" className="text-sm">EIN</Label></div>
          </RadioGroup>
        </div>
        <Field label="Last 4 of Tax ID (optional — helps admin match)" value={last4} onChange={(v) => setLast4(v.replace(/\D/g, "").slice(0, 4))} />
      </div>
      <div>
        <Label className="text-sm">Signed W-9 (PDF, max 5 MB)</Label>
        <Input
          type="file"
          accept="application/pdf"
          className="mt-1"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </div>
      <Button disabled={!file || legalName.length < 2 || uploading} onClick={upload}>
        {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</> : "Upload W-9"}
      </Button>
    </div>
  );
}

function TeachingStep({ state, call }: any) {
  const done = state.steps.teaching_agreement.complete;
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  if (done) return (
    <Card>
      <CardHeader><CardTitle>Annual Teaching Agreement</CardTitle></CardHeader>
      <CardContent>
        <Alert><AlertTitle>Signed</AlertTitle><AlertDescription>
          Year {state.steps.teaching_agreement.year} agreement signed {new Date(state.steps.teaching_agreement.signed_at).toLocaleString()}
        </AlertDescription></Alert>
      </CardContent>
    </Card>
  );
  return (
    <Card>
      <CardHeader><CardTitle>Annual Teaching Agreement</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="p-4 border rounded bg-muted/40 text-sm whitespace-pre-line max-h-64 overflow-auto">{state.teaching_agreement_text}</div>
        <div className="flex items-start gap-2">
          <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} id="ta-agree" />
          <Label htmlFor="ta-agree" className="text-sm">I agree to the annual teaching agreement.</Label>
        </div>
        <Field label="Typed legal name" value={name} onChange={setName} />
        <Field label="Electronic signature" value={sig} onChange={setSig} />
        <Button disabled={!agree || name.length < 2 || sig.length < 2}
          onClick={async () => { const r = await call({ action: "sign_teaching_agreement", signed_legal_name: name, signature: sig, agreed: true }); if (r) toast.success("Agreement signed"); }}>
          Sign & Continue
        </Button>
      </CardContent>
    </Card>
  );
}

// ConductStep removed: Standards of Conduct is now part of the Teaching
// Agreement. Historical acknowledgements remain queryable on
// `instructor_conduct_acknowledgements` for audit purposes.


function BgConsentStep({ state, call }: any) {
  const done = state.steps.bg_consent.complete;
  const [sig, setSig] = useState("");
  if (done) return (
    <Card>
      <CardHeader><CardTitle>Background Check Consent</CardTitle></CardHeader>
      <CardContent>
        <Alert><AlertTitle>Consent recorded</AlertTitle><AlertDescription>
          Silver Sage will run your annual background check through an outside provider and confirm completion.
        </AlertDescription></Alert>
      </CardContent>
    </Card>
  );
  return (
    <Card>
      <CardHeader><CardTitle>Annual Background Check Consent</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p>
          Silver Sage requires a current annual background check for every instructor.
          By signing below you consent to Silver Sage submitting your information to an
          approved background-check provider. The provider will contact you directly if
          any additional information is needed.
        </p>
        <Field label="Electronic signature" value={sig} onChange={setSig} />
        <Button disabled={sig.length < 2}
          onClick={async () => { const r = await call({ action: "bg_consent", consent_signature: sig }); if (r) toast.success("Consent recorded"); }}>
          Sign Consent
        </Button>
      </CardContent>
    </Card>
  );
}

function ReviewStep({ state, call }: any) {
  const s = state.steps;
  const items: [string, boolean][] = [
    ["Contact information", s.contact.complete],
    ["Payment information", s.payment.complete],
    ["Tax Information (W-9)", s.w9.complete],
    ["Annual teaching agreement", s.teaching_agreement.complete],
    ["Background check consent", s.bg_consent.complete],
  ];
  const readyToSubmit = s.contact.complete && s.payment.complete
    && s.w9.complete
    && s.teaching_agreement.complete && s.bg_consent.complete;
  const [confirm, setConfirm] = useState(false);
  const submitted = state.packet.overall_status === "submitted_for_review" || state.packet.overall_status === "complete";
  return (
    <Card>
      <CardHeader><CardTitle>Review & Submit</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <ul className="space-y-2">
          {items.map(([label, done]) => (
            <li key={label} className="flex items-center gap-2 text-sm">
              {done ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Clock className="h-4 w-4 text-amber-500" />}
              {label}
            </li>
          ))}
        </ul>
        {!submitted ? (
          <>
            <div className="flex items-start gap-2 pt-2">
              <Checkbox id="confirm" checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} />
              <Label htmlFor="confirm" className="text-sm">I confirm the information I have provided is accurate.</Label>
            </div>
            <Button disabled={!readyToSubmit || !confirm}
              onClick={async () => { const r = await call({ action: "submit_packet", accuracy_confirmed: true }); if (r) toast.success("Packet submitted"); }}>
              Submit Packet for Review
            </Button>
            {!readyToSubmit && (
              <p className="text-xs text-muted-foreground">Complete the remaining sections above before submitting.</p>
            )}
          </>
        ) : (
          <Alert><AlertTitle>Submitted</AlertTitle><AlertDescription>Silver Sage will contact you if anything needs changes.</AlertDescription></Alert>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, value, onChange, disabled }: { label: string; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
    </div>
  );
}
